import { IAnnouncementBar } from '@/interfaces/homepage';
import { useEffect, useRef, useState } from 'react';

export function useAnnouncement(announcementBar: IAnnouncementBar | null, isPaused: boolean) {
  const [index, setIndex] = useState(0);

  const items = announcementBar?.AnnouncementBarItems ?? [];
  const intervalTimeMS = (announcementBar?.IntervalTime ?? 0) * 1000;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const remainingTimeRef = useRef(intervalTimeMS);

  const announcement = items.length > 0 ? items[index] : undefined;

  useEffect(() => {
    if (!items.length) {
      setIndex(0);
      return;
    }

    const startTimer = () => {
      startTimeRef.current = Date.now();

      timerRef.current = setTimeout(() => {
        setIndex((prev) => (prev + 1) % items.length);
        remainingTimeRef.current = intervalTimeMS;
      }, remainingTimeRef.current);
    };

    if (isPaused) {
      if (startTimeRef.current !== null) {
        const elapsed = Date.now() - startTimeRef.current;

        remainingTimeRef.current = Math.max(0, remainingTimeRef.current - elapsed);
      }

      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      startTimeRef.current = null;

      return;
    }

    startTimer();

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isPaused, index, items.length, intervalTimeMS]);

  return { announcement };
}
